import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Briefcase,
  Upload,
  RefreshCw,
  IndianRupee,
  Package,
  Users,
  Search,
  FileSpreadsheet,
  ChevronRight,
  ArrowLeft,
  Calendar,
  Edit3,
  Save,
  X,
  CreditCard,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { DMYDateInput } from "@/components/ui/dmy-date-picker";

import { formatDMY } from "@/lib/dmy";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgentDashboard() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCode, setSelectedCode] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  
  // Upload dialog
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Ledger edit dialog
  const [showLedgerDialog, setShowLedgerDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientLedger, setClientLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [editingCredit, setEditingCredit] = useState(false);
  const [creditValue, setCreditValue] = useState("");
  const [paymentNote, setPaymentNote] = useState("");

  // Update page title
  useEffect(() => {
    document.title = `Agent Dashboard - ${user?.client_code || ""} | EAC`;
    return () => { document.title = "EAC"; };
  }, [user]);

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/agent/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setDashboardData(data);
      } else {
        toast.error(data.detail || "Failed to load dashboard");
      }
    } catch (error) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch sales data
  const fetchSales = useCallback(async () => {
    try {
      let url = `${BACKEND_URL}/api/agent/sales`;
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (selectedCode && selectedCode !== "all") params.append("code", selectedCode);
      
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setSalesData(data.sales || []);
      }
    } catch (error) {
      console.error("Error fetching sales:", error);
    }
  }, [token, dateFrom, dateTo, selectedCode]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (dashboardData) {
      fetchSales();
    }
  }, [dashboardData, fetchSales]);

  // Fetch client ledger
  const fetchClientLedger = async (code) => {
    setLedgerLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/agent/ledger/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setClientLedger(data);
        setCreditValue(data.ledger?.credit || 0);
        setPaymentNote(data.ledger?.payment_note || "");
      } else {
        toast.error(data.detail || "Failed to load ledger");
      }
    } catch (error) {
      toast.error("Failed to load ledger");
    } finally {
      setLedgerLoading(false);
    }
  };

  // Update ledger credit
  const handleUpdateCredit = async () => {
    if (!selectedClient) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/agent/ledger/${selectedClient}/update`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          credit: parseFloat(creditValue) || 0,
          payment_note: paymentNote,
          payment_date: new Date().toISOString()
        })
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success("Ledger updated successfully");
        setEditingCredit(false);
        fetchClientLedger(selectedClient);
      } else {
        toast.error(data.detail || "Failed to update ledger");
      }
    } catch (error) {
      toast.error("Failed to update ledger");
    }
  };

  // Handle bulk upload
  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await fetch(`${BACKEND_URL}/api/agent/bulk-upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        if (data.skipped_unauthorized > 0) {
          toast.warning(`${data.skipped_unauthorized} records skipped (unauthorized codes)`);
        }
        setShowUploadDialog(false);
        fetchDashboard();
        fetchSales();
      } else {
        toast.error(data.detail || "Upload failed");
      }
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Open ledger dialog
  const openLedgerDialog = (code) => {
    setSelectedClient(code);
    setShowLedgerDialog(true);
    fetchClientLedger(code);
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  // Sales by code (no profit shown)
  const salesByCode = useMemo(() => {
    const grouped = {};
    salesData.forEach(sale => {
      const code = sale.client_code || "Unknown";
      if (!grouped[code]) {
        grouped[code] = { code, orders: 0, total_sales: 0 };
      }
      grouped[code].orders += 1;
      grouped[code].total_sales += sale.sell_price || 0;
    });
    return Object.values(grouped).sort((a, b) => b.total_sales - a.total_sales);
  }, [salesData]);

  // Filtered sales
  const filteredSales = useMemo(() => {
    return salesData.filter(s => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        s.tracking_number?.toLowerCase().includes(term) ||
        s.client_code?.toLowerCase().includes(term)
      );
    });
  }, [salesData, searchTerm]);

  // All managed codes
  const managedCodes = useMemo(() => {
    return dashboardData?.managed_codes || [];
  }, [dashboardData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Briefcase className="w-7 h-7 text-purple-600" />
              Agent Dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Welcome, {user?.display_name || user?.username} ({dashboardData?.agent_code})
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => { fetchDashboard(); fetchSales(); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => setShowUploadDialog(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Bulk Upload
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-5 border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Managed Clients</p>
                <p className="text-2xl font-bold">{dashboardData?.summary?.clients_count || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Orders</p>
                <p className="text-2xl font-bold">{dashboardData?.summary?.total_orders || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <IndianRupee className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Sales</p>
                <p className="text-2xl font-bold">{formatCurrency(dashboardData?.summary?.total_sales)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="clients" className="space-y-4">
          <TabsList>
            <TabsTrigger value="clients">Managed Clients</TabsTrigger>
            <TabsTrigger value="sales">Sales Records</TabsTrigger>
          </TabsList>

          {/* Managed Clients Tab */}
          <TabsContent value="clients" className="space-y-4">
            <div className="bg-white rounded-lg border">
              {(dashboardData?.managed_clients || []).length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Users className="w-12 h-12 text-slate-300" />
                  <p className="text-slate-500 mt-4">No clients assigned</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Client Code</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="text-right">Orders</TableHead>
                      <TableHead className="text-right">Total Sales</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dashboardData.managed_clients.map((client, idx) => {
                      const codeData = salesByCode.find(s => s.code?.toUpperCase() === client.client_code?.toUpperCase()) || { orders: 0, total_sales: 0 };
                      return (
                        <TableRow key={client.client_code} className="hover:bg-slate-50">
                          <TableCell className="text-slate-500">{idx + 1}</TableCell>
                          <TableCell className="font-medium font-mono">{client.client_code}</TableCell>
                          <TableCell>{client.name || "-"}</TableCell>
                          <TableCell className="text-right">{codeData.orders}</TableCell>
                          <TableCell className="text-right font-semibold text-green-600">
                            {formatCurrency(codeData.total_sales)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-center gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => openLedgerDialog(client.client_code)}
                              >
                                <Wallet className="w-4 h-4 mr-1" />
                                Ledger
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => setSelectedCode(client.client_code)}
                              >
                                <ChevronRight className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* Sales Records Tab */}
          <TabsContent value="sales" className="space-y-4">
            {/* Filters */}
            <div className="flex items-center gap-4 bg-white p-4 rounded-lg border">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by tracking or code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={selectedCode} onValueChange={setSelectedCode}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filter by code" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Codes</SelectItem>
                  {managedCodes.map(code => (
                    <SelectItem key={code} value={code}>{code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DMYDateInput value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" />
              <DMYDateInput value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" />
            </div>

            {/* Sales Table - No Profit Column */}
            <div className="bg-white rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Tracking</TableHead>
                    <TableHead>Client Code</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Sell Price</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSales.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No sales records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSales.slice(0, 100).map((sale, idx) => (
                      <TableRow key={sale.id || idx} className="hover:bg-slate-50">
                        <TableCell className="text-slate-500">{idx + 1}</TableCell>
                        <TableCell className="font-mono text-sm">{sale.tracking_number}</TableCell>
                        <TableCell className="font-medium">{sale.client_code}</TableCell>
                        <TableCell className="text-sm">{formatDMY(sale.order_date)}</TableCell>
                        <TableCell className="text-right font-semibold text-green-600">
                          {formatCurrency(sale.sell_price)}
                        </TableCell>
                        <TableCell>
                          <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-full">
                            {sale.status || "active"}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {filteredSales.length > 100 && (
                <div className="p-3 text-center text-sm text-slate-500 bg-slate-50">
                  Showing 100 of {filteredSales.length} records
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Bulk Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Bulk Upload Sales Sheet
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="font-medium text-purple-800">Managed Clients Only</p>
              <p className="text-sm text-purple-600 mt-1">
                Only records for your managed clients ({managedCodes.join(", ")}) will be processed.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Excel File</Label>
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleBulkUpload}
                  className="hidden"
                  id="bulk-upload"
                  disabled={uploading}
                />
                <label htmlFor="bulk-upload" className="cursor-pointer">
                  <FileSpreadsheet className="w-10 h-10 mx-auto text-slate-400" />
                  <p className="mt-2 text-sm">
                    {uploading ? "Uploading..." : "Click to select Excel file"}
                  </p>
                </label>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm font-medium text-blue-800">Expected Columns:</p>
              <p className="text-xs text-blue-600">Tracking Number, Client Code (optional), Sell Price, Date (optional)</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Ledger Dialog */}
      <Dialog open={showLedgerDialog} onOpenChange={setShowLedgerDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5" />
              Ledger: {selectedClient}
            </DialogTitle>
          </DialogHeader>
          
          {ledgerLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : clientLedger ? (
            <div className="space-y-4 py-4">
              {/* Client Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="font-medium">{clientLedger.ledger?.name || selectedClient}</p>
                <p className="text-sm text-slate-500">Code: {clientLedger.ledger?.client_code}</p>
              </div>

              {/* Financial Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-600">Debit (Orders)</p>
                  <p className="text-xl font-bold text-red-700">
                    {formatCurrency(clientLedger.ledger?.debit || 0)}
                  </p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-green-600">Credit (Payments)</p>
                    {!editingCredit && (
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setEditingCredit(true)}>
                        <Edit3 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                  {editingCredit ? (
                    <div className="mt-2 space-y-2">
                      <Input
                        type="number"
                        value={creditValue}
                        onChange={(e) => setCreditValue(e.target.value)}
                        className="h-8"
                      />
                      <Input
                        placeholder="Payment note..."
                        value={paymentNote}
                        onChange={(e) => setPaymentNote(e.target.value)}
                        className="h-8"
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleUpdateCredit}>
                          <Save className="w-3 h-3 mr-1" />
                          Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingCredit(false)}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xl font-bold text-green-700">
                      {formatCurrency(clientLedger.ledger?.credit || 0)}
                    </p>
                  )}
                </div>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-600">Balance</p>
                  <p className="text-xl font-bold text-blue-700">
                    {formatCurrency((clientLedger.ledger?.debit || 0) - (clientLedger.ledger?.credit || 0))}
                  </p>
                </div>
              </div>

              {/* Recent Orders */}
              <div>
                <h4 className="font-medium mb-2">Recent Orders ({clientLedger.orders?.length || 0})</h4>
                <div className="max-h-48 overflow-y-auto border rounded-lg">
                  {(clientLedger.orders || []).length === 0 ? (
                    <p className="text-center py-4 text-slate-500 text-sm">No orders found</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Tracking</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {clientLedger.orders.slice(0, 20).map((order, idx) => (
                          <TableRow key={order.id || idx}>
                            <TableCell className="font-mono text-sm">{order.tracking_number}</TableCell>
                            <TableCell className="text-sm">{formatDMY(order.order_date)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(order.total_cost)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-center py-8 text-slate-500">No ledger data found</p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLedgerDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
